import { defineNuxtPlugin, useRuntimeConfig } from "#app";
import { nextTick } from "vue";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";
import type { Auth } from "firebase/auth";
import { initializeFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// Avoid double-init in HMR / remounts
declare global {
  var __mulberryFunctionsEmulatorConnected: boolean | undefined;

  var __mulberryAnalytics: any | undefined;
}

export default defineNuxtPlugin((nuxtApp) => {
  const { public: pub } = useRuntimeConfig();

  const firebaseConfig = {
    apiKey: pub.firebaseApiKey,
    authDomain: pub.firebaseAuthDomain,
    projectId: pub.firebaseProjectId,
    storageBucket: pub.firebaseStorageBucket,
    messagingSenderId: pub.firebaseMessagingSenderId,
    appId: pub.firebaseAppId,
    measurementId: pub.firebaseMeasurementId,
  };

  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

  // ── Suppress Firebase IndexedDB errors in hostile WebViews ──────
  // iOS in-app browsers (Instagram, Facebook) may delete IndexedDB,
  // causing Firebase to throw "Database deleted by request of the user".
  // Catching at window level prevents an unhandled-rejection Sentry event.
  window.addEventListener("unhandledrejection", (e) => {
    const msg = String(e.reason?.message || e.reason || "");
    if (
      /database deleted by request of the user/i.test(msg) ||
      /error thrown when opening indexeddb/i.test(msg)
    ) {
      e.preventDefault(); // swallow — not actionable
    }
  });

  /*
   * initializeAuth, а НЕ getAuth. Різниця одна: getAuth підставляє
   * `popupRedirectResolver: browserPopupRedirectResolver`, а той на
   * мобільних, Safari та iOS ініціалізується ПРОАКТИВНО
   * (`_shouldInitProactively = _isMobileBrowser() || _isSafari() || _isIOS()`)
   * і тягне gapi-iframe на КОЖНІЙ сторінці: apis.google.com +
   * firebaseapp.com/__/auth/iframe.js ≈ 133 KB і три зайві запити. Потрібен
   * він рівно одному сценарію — signInWithPopup для Google, і туди резолвер
   * тепер передається явно (stores/auth.ts). Десктопний Chrome цієї ціни не
   * платив, тому в мобільному звіті вона й вилізла.
   *
   * Список персистенсів — один-в-один із getAuth(): міняти порядок не можна,
   * інакше поїдуть збережені сесії.
   */
  let auth: Auth;
  try {
    auth = initializeAuth(app, {
      persistence: [
        indexedDBLocalPersistence,
        browserLocalPersistence,
        browserSessionPersistence,
      ],
    });
  } catch {
    // auth/already-initialized — HMR або повторний прогін плагіна.
    auth = getAuth(app);
  }

  // НЕ додавати сюди initializeRecaptchaConfig(auth). SDK тягне цей конфіг сам
  // у `_verifyPhoneNumber`, і робить це з важливим запобіжником: якщо фетч не
  // вдався, конфіг лишається порожнім і телефонна авторизація йде робочим
  // шляхом reCAPTCHA v2. Преload гарантував наявність конфігу завжди — і тим
  // самим прибирав цей аварійний вихід.

  const db = initializeFirestore(app, {
    ignoreUndefinedProperties: true,
    experimentalAutoDetectLongPolling: true,
  });
  const storage = getStorage(app);

  // Functions (callable + webhook helpers)
  const functions = getFunctions(
    app,
    String(pub.firebaseFunctionsRegion || "us-central1"),
  );

  // Optional local emulator support (only if configured)
  if (import.meta.client) {
    const host = pub.firebaseFunctionsEmulatorHost as string | undefined;
    const port = Number(pub.firebaseFunctionsEmulatorPort || 5001);
    if (host && !globalThis.__mulberryFunctionsEmulatorConnected) {
      connectFunctionsEmulator(functions, host, port);
      globalThis.__mulberryFunctionsEmulatorConnected = true;
    }
  }

  // -----------------------------
  // 🔵 Firebase Analytics (GA4)
  // Best practice:
  // - client-only
  // - respect cookie consent (prefs.analytics)
  // - avoid noisy dev traffic (disabled on localhost unless debug flag)
  // - SPA page_view on route changes
  // -----------------------------

  const COOKIE_PREFS_VERSION = "v1"; // must match cookie-consent + /cookies page

  function readCookiePrefs(): { analytics?: boolean; marketing?: boolean } {
    try {
      const v = localStorage.getItem("cookiePrefsVersion");
      const raw = localStorage.getItem("cookiePrefs");
      if (raw && v === COOKIE_PREFS_VERSION) return JSON.parse(raw);
    } catch {}
    return { analytics: false, marketing: false };
  }

  function isAnalyticsAllowedByHost(): boolean {
    const host = location.hostname;
    const isProd = ["mulberryss.shop", "www.mulberryss.shop"].includes(host);

    // Allow local testing ONLY if explicitly enabled
    const qs = new URLSearchParams(location.search);
    const debugFromQuery = qs.get("ga_debug") === "1";
    const debugFromStorage = localStorage.getItem("ga_debug") === "1";
    const isLocal = ["localhost", "127.0.0.1"].includes(host);

    return isProd || (isLocal && (debugFromQuery || debugFromStorage));
  }

  async function ensureAnalytics() {
    if (!pub.firebaseMeasurementId) return null;
    if (typeof window === "undefined") return null;
    if (!isAnalyticsAllowedByHost()) return null;

    const prefs = readCookiePrefs();

    // If user didn't consent — do not collect
    if (prefs.analytics !== true) {
      if (globalThis.__mulberryAnalytics) {
        try {
          const m = await import("firebase/analytics");
          m.setAnalyticsCollectionEnabled(
            globalThis.__mulberryAnalytics,
            false,
          );
        } catch {}
      }
      return null;
    }

    // Already initialized
    if (globalThis.__mulberryAnalytics) {
      try {
        const m = await import("firebase/analytics");
        m.setAnalyticsCollectionEnabled(globalThis.__mulberryAnalytics, true);
      } catch {}
      return globalThis.__mulberryAnalytics;
    }

    // Init lazily (keeps bundle smaller, avoids SSR issues)
    try {
      const m = await import("firebase/analytics");
      const supported = await m.isSupported();
      if (!supported) return null;

      const analytics = m.getAnalytics(app);
      globalThis.__mulberryAnalytics = analytics;

      try {
        m.setAnalyticsCollectionEnabled(analytics, true);
      } catch {}

      return analytics;
    } catch {
      return null;
    }
  }

  async function logPageView(path?: string) {
    const analytics = await ensureAnalytics();
    if (!analytics) return;

    try {
      const m = await import("firebase/analytics");
      const page_path = path || location.pathname + location.search;
      m.logEvent(analytics, "page_view", {
        page_title: document.title,
        page_location: location.href,
        page_path,
      });
    } catch {}
  }

  // Track SPA navigations
  if (import.meta.client) {
    nuxtApp.hook("app:mounted", async () => {
      // Initial page_view (after title/meta applied)
      await nextTick();
      await logPageView(
        (nuxtApp.$router as any)?.currentRoute?.value?.fullPath,
      );

      // Subsequent navigations
      // $router injection is untyped until the TS pass (REFACTOR_PLAN п.13).
      const router = nuxtApp.$router as any;
      router?.afterEach(async (to: any) => {
        await nextTick();
        await logPageView(to.fullPath);
      });
    });

    // React to consent changes without requiring a hard reload
    window.addEventListener("cookiePrefsUpdated", async () => {
      await nextTick();
      await logPageView(
        (nuxtApp.$router as any)?.currentRoute?.value?.fullPath,
      );
    });

    // Allow enabling debug on localhost via query param
    // Example: http://localhost:3000/login?ga_debug=1
    try {
      const qs = new URLSearchParams(location.search);
      if (qs.get("ga_debug") === "1") localStorage.setItem("ga_debug", "1");
    } catch {}
  }

  // Helper for custom events later (add_to_cart, begin_checkout, purchase, etc.)
  const ga = {
    async log(name: string, params?: Record<string, any>) {
      const analytics = await ensureAnalytics();
      if (!analytics) return false;
      try {
        const m = await import("firebase/analytics");
        m.logEvent(analytics, name, params);
        return true;
      } catch {
        return false;
      }
    },
  };

  return { provide: { auth, db, storage, functions, ga } };
});
